import React, { useCallback, useState } from 'react';
import { Radio, Space, RadioChangeEvent } from 'antd';
import { CopyOutlined, SaveOutlined } from '@ant-design/icons';
import styles from './SaveWidgetTypeModal.module.css';
import RoundedModal from '@components/roundedModal/roundedModal';
import useTranslation from '@hooks/useTranslation';

type Props = {
	visible: boolean;
	onClose: () => void;
	onCreateNew: () => void;
	onUpdateOriginal: () => void;
}

type SaveOption = 'new' | 'original';

const SaveWidgetTypeModal = ({ visible, onClose, onCreateNew, onUpdateOriginal }: Props) => {
	const t = useTranslation('CustomWidgetModal.SaveModal');
	const cw = useTranslation('CommonWords');
	const [selectedOption, setSelectedOption] = useState<SaveOption>('new');
	const [saving, setSaving] = useState(false);

	const handleSave = useCallback(() => {
		setSaving(true);
		try {
			if (selectedOption === 'new') {
				onCreateNew();
			} else {
				onUpdateOriginal();
			}
			onClose();
		} finally {
			setSaving(false);
		}
	}, [selectedOption, onCreateNew, onUpdateOriginal, onClose]);

	return (
		<RoundedModal
			title={t('Title')}
			visible={visible}
			onCancel={onClose}
			width={500}
			closeOnMaskClick={false}
			closable={true}
			loadingOkBtn={saving}
			okBtnLabel={cw('Save')}
			cancelBtnLabel={cw('Cancel')}
			onOk={handleSave}
		>
			<div className={styles.Content}>
				<div className={styles.Description}>
					{t('Description')}
				</div>

				<Radio.Group
					value={selectedOption}
					onChange={(e: RadioChangeEvent) => setSelectedOption(e.target.value)}
					className={styles.OptionsGroup}
				>
					<Space direction="vertical" size="large" style={{ width: '100%', marginBottom: 20 }}>
						<Radio value="new" className={styles.Option}>
							<Space>
								<CopyOutlined className={styles.OptionIcon} />
								<div>
									<div className={styles.OptionTitle}>{t('New.Title')}</div>
									<div className={styles.OptionDescription}>
										{t('New.Description')}
									</div>
								</div>
							</Space>
						</Radio>

						<Radio value="original" className={styles.Option}>
							<Space>
								<SaveOutlined className={styles.OptionIcon} />
								<div>
									<div className={styles.OptionTitle}>{t('Original.Title')}</div>
									<div className={styles.OptionDescription}>
										{t('Original.Description')}
									</div>
								</div>
							</Space>
						</Radio>
					</Space>
				</Radio.Group>
			</div>
		</RoundedModal>
	);
};

export default SaveWidgetTypeModal;
